import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Plus,
  Edit,
  Trash2
} from 'lucide-react';
import { TabLayout, ContentCard } from '../../components/ui/TabLayout';
import { ResponsiveTable, StatusBadge } from '../../components/ui/table';
import { Button } from '../../components/ui/Button/Button';
import { Filter } from '../../components/ui/Filter';
import { Pagination } from '../../components/ui/Pagination';
import { approveDriver, rejectDriver, updateDriver, deleteDriver } from '../../store/driverSlice';
import { AddDriverModal } from '../../components/modals/AddDriverModal';
import { EditDriverModal } from '../../components/modals/EditDriverModal';
import { ApproveDriverDialog, RejectDriverDialog, DeleteDriverDialog } from '../../components/modals/ConfirmationDialog';
import { useToast } from '../../components/ui/Toast';

const PAGE_SIZE = 10;

export function DriverApplicationsPage() {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();
  const { drivers, pendingRegistrations, isLoading } = useSelector(state => state.drivers);

  const [activeTab, setActiveTab] = useState('pending');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // Confirmation dialog states
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Action handlers
  const handleEdit = (driver) => {
    setSelectedDriver(driver);
    setShowEditModal(true);
  };

  const handleDelete = (driver) => {
    setSelectedDriver(driver);
    setShowDeleteDialog(true);
  };

  const handleApprove = (driver) => {
    setSelectedDriver(driver);
    setShowApproveDialog(true);
  };

  const handleReject = (driver) => {
    setSelectedDriver(driver);
    setShowRejectDialog(true);
  };

  // Confirmation handlers
  const confirmApprove = async (reason) => {
    if (!selectedDriver) return;

    try {
      const result = await dispatch(approveDriver({
        driverId: selectedDriver.id,
        adminNotes: reason || 'Approved via admin panel'
      }));

      if (result.success !== false) {
        showSuccess('Success', `${selectedDriver.personalInfo?.firstName || selectedDriver.name} has been approved`);
      } else {
        showError('Error', 'Failed to approve driver');
      }
    } catch (error) {
      showError('Error', 'An unexpected error occurred');
    } finally {
      setShowApproveDialog(false);
      setSelectedDriver(null);
    }
  };

  const confirmReject = async (reason) => {
    if (!selectedDriver) return;

    try {
      const result = await dispatch(rejectDriver({
        driverId: selectedDriver.id,
        reason: reason || 'Application rejected',
        adminNotes: reason || 'Rejected via admin panel'
      }));

      if (result.success !== false) {
        showSuccess('Success', `${selectedDriver.personalInfo?.firstName || selectedDriver.name}'s application has been rejected`);
      } else {
        showError('Error', 'Failed to reject driver');
      }
    } catch (error) {
      showError('Error', 'An unexpected error occurred');
    } finally {
      setShowRejectDialog(false);
      setSelectedDriver(null);
    }
  };

  const confirmDelete = async (reason) => {
    if (!selectedDriver) return;

    try {
      const result = await dispatch(deleteDriver(selectedDriver.id, {
        softDelete: true,
        reason: reason || 'Deleted via admin panel'
      }));

      if (result.success) {
        showSuccess('Success', `${selectedDriver.personalInfo?.firstName || selectedDriver.name} has been deleted`);
      } else {
        showError('Error', result.error || 'Failed to delete driver');
      }
    } catch (error) {
      showError('Error', 'An unexpected error occurred');
    } finally {
      setShowDeleteDialog(false);
      setSelectedDriver(null);
    }
  };

  const pendingColumns = [
    {
      key: 'name',
      header: 'Driver Name',
      primary: true,
      render: (_, item) => {
        const name = item?.name || `${item?.personalInfo?.firstName || ''} ${item?.personalInfo?.lastName || ''}`.trim();
        const email = item?.email || item?.personalInfo?.email || '';
        return (
          <div>
            <div className="font-medium text-gray-900">{name}</div>
            <div className="text-sm text-gray-500">{email}</div>
          </div>
        );
      }
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (_, item) => item?.phone || item?.personalInfo?.phone || 'N/A'
    },
    {
      key: 'appliedAt',
      header: 'Applied Date',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'vehicleInfo',
      header: 'Vehicle',
      render: (value) => value ? `${value.make} ${value.model} (${value.year})` : 'Not provided'
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => (
        <StatusBadge 
          status={value} 
          variant={
            value === 'pending' ? 'warning' :
            value === 'approved' ? 'success' :
            value === 'rejected' ? 'error' : 'default'
          } 
        />
      )
    }
  ];

  const approvedColumns = [
    ...pendingColumns,
    {
      key: 'approvedAt',
      header: 'Approved Date',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-'
    }
  ];

  const pendingActions = [
    {
      type: 'button',
      onClick: (item) => handleApprove(item),
      label: 'Approve',
      variant: 'success',
      icon: CheckCircle
    },
    {
      type: 'button',
      onClick: (item) => handleReject(item),
      label: 'Reject',
      variant: 'error',
      icon: XCircle
    },
    {
      type: 'button',
      onClick: (item) => handleEdit(item),
      label: 'Edit',
      variant: 'secondary',
      icon: Edit
    },
    {
      type: 'button',
      onClick: (item) => handleDelete(item),
      label: 'Delete',
      variant: 'error',
      icon: Trash2
    }
  ];

  const approvedActions = [
    {
      type: 'button',
      onClick: (item) => handleEdit(item),
      label: 'Edit',
      variant: 'secondary',
      icon: Edit
    },
    {
      type: 'button',
      onClick: (item) => handleDelete(item),
      label: 'Delete',
      variant: 'error',
      icon: Trash2
    }
  ];

  const filteredPending = pendingRegistrations?.filter(item => {
    const name = item?.name || `${item?.personalInfo?.firstName || ''} ${item?.personalInfo?.lastName || ''}`.trim();
    const email = item?.email || item?.personalInfo?.email || '';
    return (
      (name.toLowerCase().includes(filter.toLowerCase()) || false) ||
      (email.toLowerCase().includes(filter.toLowerCase()) || false)
    );
  });

  const filteredApproved = drivers?.filter(item => {
    const name = item?.name || `${item?.personalInfo?.firstName || ''} ${item?.personalInfo?.lastName || ''}`.trim();
    const email = item?.email || item?.personalInfo?.email || '';
    return (
      item?.status === 'approved' &&
      ((name.toLowerCase().includes(filter.toLowerCase()) || false) ||
       (email.toLowerCase().includes(filter.toLowerCase()) || false))
    );
  });

  const getCurrentData = () => {
    const data = activeTab === 'pending' ? filteredPending : filteredApproved;
    const totalPages = Math.ceil(data.length / PAGE_SIZE);
    const start = (page - 1) * PAGE_SIZE;
    const pageItems = data.slice(start, start + PAGE_SIZE);
    
    return { data: pageItems, totalPages };
  };

  const { data: currentData, totalPages } = getCurrentData();

  const tabs = [
    { id: 'pending', label: 'Pending Applications', icon: Clock },
    { id: 'approved', label: 'Approved Drivers', icon: CheckCircle },
    { id: 'rejected', label: 'Rejected Applications', icon: XCircle }
  ];

  const actionButton = (
    <Button
      variant="primary"
      onClick={() => setShowAddModal(true)}
    >
      <Plus size={16} className="mr-2" />
      Add Driver
    </Button>
  );

  return (
    <>
    <TabLayout
      title="Driver Applications"
      subtitle="Review and manage driver applications and approvals"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      actionButton={actionButton}
    >
      {/* Filter and Search */}
      
          <Filter
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name or email..."
          />
        

      {/* Applications Table */}
      <ResponsiveTable
        data={currentData}
        columns={activeTab === 'pending' ? pendingColumns : approvedColumns}
        actions={activeTab === 'pending' ? pendingActions : approvedActions}
        emptyState={
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Clock size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No {activeTab} applications
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              There are currently no {activeTab} driver applications.
            </p>
          </div>
        }
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          current={page}
          total={totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Statistics Card */}
      <ContentCard title="Application Statistics">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {pendingRegistrations.length}
            </div>
            <p className="text-sm text-gray-600 mt-1">Pending Review</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {drivers.filter(d => d.status === 'approved').length}
            </div>
            <p className="text-sm text-gray-600 mt-1">Approved</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {drivers.filter(d => d.status === 'rejected').length}
            </div>
            <p className="text-sm text-gray-600 mt-1">Rejected</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {((drivers.filter(d => d.status === 'approved').length / 
                 (drivers.length + pendingRegistrations.length)) * 100).toFixed(1)}%
            </div>
            <p className="text-sm text-gray-600 mt-1">Approval Rate</p>
          </div>
        </div>
      </ContentCard>
    </TabLayout>

    {/* Modals */}
    <AddDriverModal
      isOpen={showAddModal}
      onClose={() => setShowAddModal(false)}
    />

    <EditDriverModal
      isOpen={showEditModal}
      onClose={() => setShowEditModal(false)}
      driver={selectedDriver}
    />

    {/* Confirmation Dialogs */}
    <ApproveDriverDialog
      isOpen={showApproveDialog}
      onClose={() => setShowApproveDialog(false)}
      onConfirm={confirmApprove}
      driverName={selectedDriver?.personalInfo?.firstName || selectedDriver?.name || 'Driver'}
      isLoading={isLoading}
    />

    <RejectDriverDialog
      isOpen={showRejectDialog}
      onClose={() => setShowRejectDialog(false)}
      onConfirm={confirmReject}
      driverName={selectedDriver?.personalInfo?.firstName || selectedDriver?.name || 'Driver'}
      isLoading={isLoading}
    />

    <DeleteDriverDialog
      isOpen={showDeleteDialog}
      onClose={() => setShowDeleteDialog(false)}
      onConfirm={confirmDelete}
      driverName={selectedDriver?.personalInfo?.firstName || selectedDriver?.name || 'Driver'}
      isLoading={isLoading}
    />
    </>
  );
}
